import type { AdminCatalogEvent } from '@steado/admin-contract/catalog-history';
import type { AdminFoodRequest, AdminFoodRequestDetail } from '@steado/admin-contract/foods';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertCircleIcon, ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import { useId, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import {
  eventChangesQueryOptions,
  invalidateCatalog,
  revertBatch,
  subjectEventsQueryOptions,
} from '@/screens/catalog-history/catalog-history-query';
import { actorName, eventTitle, requestIdOf } from '@/screens/catalog-history/event-copy';
import { BeforeAfter, Figures, SourceList, StatusBanner } from '@/screens/catalog-history/history-parts';
import { labelWhen } from '@/screens/catalog-history/history-model';
import { SourceChip } from '@/screens/catalog-history/SourceChip';
import { foodQueryOptions } from '@/screens/foods/foods-query';

import { answerGroups, retiredEstimate } from './answered-model';
import { invalidateRequests, queueQueryOptions, reopenFoodRequest } from './food-requests-query';
import { KIND_LABELS, SOURCE_NAMES, ago, clock, outcomeOf, plural } from './request-copy';
import { ADMIN_ONLY, Card, KindBadge } from './review-parts';

/** What an answer did, named by kind: "Applied" for a fix, "Created" for a new food. */
const DONE: Readonly<Record<AdminFoodRequest['kind'], { verb: string; noun: string }>> = {
  fix: { verb: 'Applied', noun: 'fix' },
  new_food: { verb: 'Approved', noun: 'approval' },
  name: { verb: 'Added', noun: 'name' },
};

/**
 * A request once it is answered: what its answer wrote, live in the catalog,
 * with Revert; the food's History beside it; and the other open requests for
 * the same food, whose drafts may now be out of date. Once reverted (or
 * rejected), the request can go back in the queue.
 */
export function AnsweredView({
  request,
  onReopened,
}: {
  readonly request: AdminFoodRequestDetail;
  readonly onReopened: () => void;
}) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const questionId = useId();
  const foodId = request.resolvedFoodId;
  const food = useQuery(foodQueryOptions(api, foodId));
  const slug = food.data?.slug ?? null;
  const events = useQuery({
    ...subjectEventsQueryOptions(api, 'food', slug ?? ''),
    enabled: slug !== null,
  });
  const approval = (events.data ?? []).find((event) => event.source === `food_request:${request.id}`);
  const batchId = approval?.batchId ?? null;
  const changes = useQuery(eventChangesQueryOptions(api, batchId));
  const revertEvent = (events.data ?? []).find(
    (event) => event.kind === 'revert' && event.revertOf?.batchId === batchId && batchId !== null,
  );
  const reverted = approval?.batch?.status === 'reverted' || revertEvent !== undefined;
  const queue = useQuery(queueQueryOptions(api, { status: 'open' }));
  const name = food.data?.displayName ?? food.data?.name ?? request.resolvedFoodName ?? request.name;
  const done = DONE[request.kind];
  const next = (queue.data ?? []).find(
    (other) => other.kind === request.kind && other.draftStatus !== 'queued' && other.id !== request.id,
  );
  const others = (queue.data ?? []).filter(
    (other) =>
      other.id !== request.id && other.foodId !== null && other.foodId === (request.foodId ?? foodId),
  );

  const refresh = () => {
    invalidateRequests(queryClient);
    invalidateCatalog(queryClient);
  };

  const revert = useMutation({
    mutationFn: () => revertBatch(api, batchId ?? ''),
    onSuccess: () => {
      setConfirming(false);
      refresh();
    },
    retry: false,
  });

  const reopen = useMutation({
    mutationFn: () => reopenFoodRequest(api, request.id),
    onSuccess: () => {
      refresh();
      onReopened();
    },
    retry: false,
  });

  const groups = answerGroups(changes.data ?? [], {
    food: { slug, name },
    estimate: retiredEstimate(request),
    moves: request.estimateMoves,
    batchId,
    reverted,
    names: new Map(
      (approval?.subjects ?? []).flatMap((subject) =>
        subject.name === null ? [] : [[subject.slug, subject.name] as const],
      ),
    ),
  });
  const status =
    request.status === 'rejected'
      ? 'Rejected'
      : reverted
        ? 'Reverted'
        : approval === undefined
          ? 'Answered'
          : done.verb;

  return (
    <div className="mx-auto flex w-full max-w-[1300px] flex-col gap-3.5">
      <p className="text-xs text-muted-foreground">
        {KIND_LABELS[request.kind]} request · {status.toLowerCase()}
      </p>

      {request.status === 'rejected' ? (
        <StatusBanner
          tone="mute"
          action={
            <ReopenButton
              canMutate={can.mutate}
              isPending={reopen.isPending}
              onReopen={() => {
                reopen.mutate();
              }}
            />
          }
        >
          <span className="font-medium">
            Rejected {request.answeredAt === null ? '' : ago(request.answeredAt)}
          </span>
          <span className="text-muted-foreground">
            {' '}
            · {request.rejectionReason ?? 'No reason given'}. Nothing in the catalog changed.
          </span>
        </StatusBanner>
      ) : reverted ? (
        <StatusBanner
          tone="mute"
          action={
            <ReopenButton
              canMutate={can.mutate}
              isPending={reopen.isPending}
              onReopen={() => {
                reopen.mutate();
              }}
            />
          }
        >
          <span className="font-medium">
            Reverted by {revertEvent === undefined ? 'someone' : actorName(revertEvent.actor)}
            {revertEvent === undefined ? '' : ` at ${clock(revertEvent.endedAt)}`}
          </span>
          <span className="text-muted-foreground">
            {' '}
            · {name} has its values from before the {done.noun} again. Both changes are in History.
          </span>
        </StatusBanner>
      ) : confirming ? (
        <div
          role="alertdialog"
          aria-labelledby={questionId}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-input bg-card py-2 pr-2 pl-3.5"
        >
          <span className="flex min-w-0 flex-[1_1_420px] flex-col gap-0.5">
            <span id={questionId} className="font-medium text-foreground">
              Revert this {done.noun}?
            </span>
            <span className="text-xs text-muted-foreground">
              {name} goes back to how it was before it, value by value. The revert is saved as a new change in
              History.
            </span>
            {revert.isError ? (
              <span role="alert" className="text-xs font-medium text-err-fg">
                {revert.error.message}
              </span>
            ) : null}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfirming(false);
              revert.reset();
            }}
          >
            Keep the {done.noun}
          </Button>
          <Button
            variant="outline"
            size="sm"
            autoFocus
            disabled={revert.isPending}
            className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
            onClick={() => {
              revert.mutate();
            }}
          >
            {revert.isPending ? 'Reverting…' : `Revert ${done.noun}`}
          </Button>
        </div>
      ) : (
        <StatusBanner
          action={
            next === undefined ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/food-requests">Back to food requests</Link>
              </Button>
            ) : (
              <Button size="sm" className="font-semibold" asChild>
                <Link to="/food-requests/$requestId" params={{ requestId: next.id }}>
                  Next {KIND_LABELS[next.kind].toLowerCase()}: {next.name}
                  <ArrowRightIcon aria-hidden="true" className="size-3.5" />
                </Link>
              </Button>
            )
          }
        >
          <span className="inline-flex flex-wrap items-center gap-x-1.5">
            <span className="font-medium">
              {done.verb} by {approval === undefined ? 'an admin' : actorName(approval.actor)}
              {request.answeredAt === null ? '' : ' at '}
              {request.answeredAt === null ? null : (
                <span className="font-mono tabular-nums">{clock(request.answeredAt)}</span>
              )}
            </span>
            {batchId === null ? null : (
              <>
                <span aria-hidden="true" className="text-muted-foreground">
                  ·
                </span>
                <button
                  type="button"
                  aria-describedby={can.mutate ? undefined : questionId}
                  disabled={!can.mutate}
                  onClick={() => {
                    setConfirming(true);
                  }}
                  className="inline-flex h-8 items-center px-1 font-medium text-foreground underline underline-offset-[3px] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Revert
                </button>
                {can.mutate ? null : (
                  <span id={questionId} className="sr-only">
                    {ADMIN_ONLY}
                  </span>
                )}
              </>
            )}
          </span>
          <span className="text-xs text-ok-fg">
            {' '}
            Live in the catalog now. The {plural(request.userCount, 'person', 'people')} who asked{' '}
            {request.userCount === 1 ? 'sees' : 'see'} it the next time they log {name.toLowerCase()}.
          </span>
        </StatusBanner>
      )}

      {reopen.isError ? (
        <p role="alert" className="text-[13px] font-medium text-err-fg">
          {reopen.error.message}
        </p>
      ) : null}

      <section
        aria-labelledby="food-title"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        <div className="flex flex-wrap items-start gap-3.5 px-[18px] pt-4 pb-3.5">
          <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 id="food-title" className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
                {request.status === 'rejected' ? request.name : name}
              </h1>
              <KindBadge kind={request.kind} />
              <Badge size="sm" tone={status === 'Rejected' || status === 'Reverted' ? 'mute' : 'ok'}>
                {status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {food.data === undefined
                ? outcomeOf(request)
                : `${SOURCE_NAMES[food.data.source]}${food.data.isVerified ? ', verified' : ''} · ${food.data.slug}`}{' '}
              · asked by {plural(request.userCount, 'person', 'people')}
            </p>
          </div>
          {foodId === null ? null : (
            <Button variant="outline" asChild>
              <Link to="/foods/$foodId" params={{ foodId }} search={{ tab: 'details' }}>
                Open {name} in the catalog
              </Link>
            </Button>
          )}
        </div>
      </section>

      <div className="flex flex-wrap items-start gap-3.5">
        <Card
          title="What changed"
          description={
            approval === undefined
              ? undefined
              : `${plural(approval.valueCount, 'value')}, one change in History`
          }
          className="flex-[13_1_480px]"
        >
          {request.status === 'rejected' ? (
            <p className="border-t border-border px-[18px] py-2.5 text-[13px] text-muted-foreground">
              Nothing: a rejected request leaves the catalog as it was.
            </p>
          ) : approval === undefined ? (
            <p className="border-t border-border px-[18px] py-2.5 text-[13px] text-muted-foreground">
              {events.isPending && slug !== null
                ? 'Loading…'
                : `${outcomeOf(request)}. It wrote no History batch of its own.`}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="border-t border-border pb-1.5">
                {groups.length > 1 ? (
                  <h3 className="px-[18px] pt-2.5 pb-0.5 text-[13px] font-semibold text-foreground">
                    {group.title}
                  </h3>
                ) : null}
                {group.rows.map((row) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[minmax(160px,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3 px-[18px] py-1 text-[13px]"
                  >
                    <span className="text-muted-foreground">{row.field}</span>
                    {row.before === null ? (
                      <Figures text={row.after} className="min-w-0 font-medium text-pretty text-foreground" />
                    ) : (
                      <BeforeAfter before={row.before} after={row.after} />
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
          {approval === undefined ? null : (
            <div className="flex flex-col gap-1 border-t border-border bg-secondary/40 px-[18px] py-3 text-xs text-muted-foreground">
              {approval.sources.length === 0 ? (
                <span>Saved without sources.</span>
              ) : (
                <>
                  <span>Saved with {plural(approval.sources.length, 'source')}</span>
                  <SourceList sources={approval.sources} />
                </>
              )}
              {request.kind === 'fix' ? (
                <span>
                  Past logs keep the numbers they were saved with. New logs use the fix from now on.
                </span>
              ) : null}
            </div>
          )}
        </Card>

        <Card
          title={`Other open requests for ${name}`}
          description="Drafts made before this answer are checked against the food as it is now."
          className="flex-[10_1_380px]"
        >
          {others.length === 0 ? (
            <p className="border-t border-border px-[18px] py-2.5 text-[13px] text-muted-foreground">
              No other request for {name} is open.
            </p>
          ) : (
            others.map((other) => {
              const stale =
                other.researchedAt !== null &&
                request.answeredAt !== null &&
                other.researchedAt < request.answeredAt &&
                !reverted;
              return (
                <div
                  key={other.id}
                  className={cn(
                    'flex flex-col gap-2 border-t border-border px-[18px] pt-3 pb-3.5',
                    stale && 'bg-err-bg/40',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Link
                      to="/food-requests/$requestId"
                      params={{ requestId: other.id }}
                      className="inline-flex min-h-6 items-center font-medium text-foreground no-underline hover:text-destructive"
                    >
                      {other.name}
                    </Link>
                    <KindBadge kind={other.kind} />
                    <span className="text-xs text-muted-foreground">
                      {plural(other.userCount, 'person', 'people')}
                      {other.researchedAt === null ? '' : ` · drafted ${ago(other.researchedAt)}`}
                    </span>
                  </div>
                  {stale ? (
                    <div role="alert" className="flex items-start gap-2 text-[13px] text-foreground">
                      <AlertCircleIcon
                        aria-hidden="true"
                        className="mt-0.5 size-[15px] shrink-0 text-err-fg"
                      />
                      <span>{name} changed since this was drafted. Review again.</span>
                    </div>
                  ) : null}
                  <Button variant="outline" size="sm" className="self-start" asChild>
                    <Link to="/food-requests/$requestId" params={{ requestId: other.id }}>
                      <RotateCcwIcon aria-hidden="true" className="size-3.5" />
                      Review again
                    </Link>
                  </Button>
                </div>
              );
            })
          )}
        </Card>
      </div>

      {foodId === null || slug === null ? null : (
        <FoodHistory
          name={name}
          foodId={foodId}
          events={events.data ?? []}
          requestId={request.id}
          canRevert={can.mutate && !reverted && batchId !== null}
          onRevert={() => {
            setConfirming(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </div>
  );
}

function ReopenButton({
  canMutate,
  isPending,
  onReopen,
}: {
  readonly canMutate: boolean;
  readonly isPending: boolean;
  readonly onReopen: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!canMutate || isPending}
      title={canMutate ? undefined : ADMIN_ONLY}
      onClick={onReopen}
    >
      {isPending ? 'Opening…' : 'Open the request again'}
    </Button>
  );
}

/** The food's latest changes, newest first, with this request's own change revertible from here. */
function FoodHistory({
  name,
  foodId,
  events,
  requestId,
  canRevert,
  onRevert,
}: {
  readonly name: string;
  readonly foodId: string;
  readonly events: readonly AdminCatalogEvent[];
  readonly requestId: string;
  readonly canRevert: boolean;
  readonly onRevert: () => void;
}) {
  const shown = events.slice(0, 4);
  const older = events.length - shown.length;
  return (
    <Card
      title={`${name}’s History`}
      description="Newest first. Each change can be reverted."
      action={
        <Link
          to="/foods/$foodId"
          params={{ foodId }}
          search={{ tab: 'history' }}
          className="inline-flex h-8 items-center gap-1 text-[13px] font-medium text-info-fg no-underline hover:underline"
        >
          Open full History
          <ArrowRightIcon aria-hidden="true" className="size-3.5" />
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <div role="table" aria-label={`${name}’s History, newest first`} className="min-w-[820px]">
          <div
            role="row"
            className="grid grid-cols-[116px_minmax(0,1fr)_120px_120px_120px] items-center gap-3 border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase"
          >
            <span role="columnheader">Source</span>
            <span role="columnheader">Change</span>
            <span role="columnheader">Who</span>
            <span role="columnheader">When</span>
            <span role="columnheader" className="text-right">
              Revert
            </span>
          </div>
          {shown.map((event) => {
            const own = requestIdOf(event, event.source) === requestId && event.kind === 'request';
            return (
              <div
                key={event.key}
                role="row"
                className={cn(
                  'grid grid-cols-[116px_minmax(0,1fr)_120px_120px_120px] items-center gap-3 border-t border-border px-[18px] py-2.5 text-[13px]',
                  own ? 'bg-secondary/50' : 'bg-card',
                )}
              >
                <span role="cell">
                  <SourceChip kind={event.kind} />
                </span>
                <span role="cell" className="flex min-w-0 flex-col gap-px">
                  <span className="text-foreground">
                    {eventTitle({ kind: event.kind, source: event.source, event, values: event.valueCount })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {plural(event.valueCount, 'value')}
                    {event.sources.length === 0
                      ? ' · no sources'
                      : ` · ${plural(event.sources.length, 'source')}`}
                  </span>
                </span>
                <span role="cell">{actorName(event.actor)}</span>
                <span role="cell" className="text-muted-foreground">
                  <Figures text={labelWhen(event.endedAt)} />
                </span>
                <span role="cell" className="flex justify-end">
                  {own && canRevert ? (
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={`Revert: ${eventTitle({ kind: event.kind, source: event.source, event, values: event.valueCount })}`}
                      onClick={onRevert}
                    >
                      Revert
                    </Button>
                  ) : null}
                </span>
              </div>
            );
          })}
          {older <= 0 ? null : (
            <div
              role="row"
              className="border-t border-border bg-secondary/40 px-[18px] py-2.5 text-xs text-muted-foreground"
            >
              <span role="cell">
                {plural(older, 'older change')} {older === 1 ? 'is' : 'are'} in the full History.
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
